<template>
  <div class="grid-x flex-grow">
    <div class="cell auto cell-block-y flex flex-col flex-grow">
      <div class="flex flex-col flex-grow">
        <div class="flex-grow">
          <Loading :loading="waitPending">
            <Table
              :columns="columns"
              :rows="customers"
              :row-style-class="() => 'cursor-pointer'"
              @on-row-click="onRowClick"
              @on-sort-change="($event) => {orderBy = $event}"/>
          </Loading>
        </div>
        <div class="flex flex-row items-end justify-center p-6">
          <Pagination
            :page="page"
            :page-count="pageCount"
            @change="onPageChange"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script type="text/jsx">
import _ from 'lodash';
import { mapActions, mapState } from 'vuex';
import Table from '@/components/Table.vue';
import Pagination from '@/components/Pagination.vue';
import Loading from '@/components/Loading.vue';

export default {
  components: {
    Table,
    Pagination,
    Loading,
  },
  data() {
    const routeQuery = this.$route.query || {};
    const sort = routeQuery.sort || null;
    const dir = routeQuery.dir || 'desc';

    return {
      limit: 100,
      page: Math.max(1, Number(routeQuery.page || 1) || 1),
      orderBy: sort ? [{ column: sort, descending: dir === 'desc' ? 1 : 0 }] : [],
      query: String(routeQuery.search || ''),
      syncRouteLocked: false,
    };
  },
  computed: {
    ...mapState({
      payload: (state) => state.customers.data,
    }),
    columns() {
      return [
        {
          label: 'ID',
          field: 'id',
          tdClass: 'font-bold',
          sortable: true,
        },
        {
          label: 'Регистрация',
          field: 'registrationDate',
          tdClass: 'font-bold',
          cell: ({ row }) => <b>
            { this.$moment(row.registrationDate, 'YYYY-MM-DD').format('DD.MM.YYYY') }
          </b>,
          sortable: true,
        },
        {
          label: 'ФИО',
          field: 'name',
          sortable: true,
        },
        {
          label: 'Номер телефона',
          field: 'phone',
          sortable: false,
        },
        {
          label: 'Кол-во объектов',
          field: 'ownedSubjectsCount',
          tdClass: 'font-bold',
          cell: ({ row }) => <b>{ row.subjectsCount }</b>,
          sortable: true,
        },
        {
          label: 'Последний заказ',
          field: 'lastOrderCreatedAt',
          tdClass: 'font-bold',
          cell: ({ row }) => {
            if (row.lastOrderDate) {
              return <b>
                { this.$moment(row.lastOrderDate, 'YYYY-MM-DD').format('DD.MM.YYYY') }
              </b>;
            }
            return null;
          },
          sortable: true,
        },
        {
          label: 'Сумма заказа',
          field: 'lastOrderPrice',
          tdClass: 'font-bold',
          cell: ({ row }) => {
            if (row.lastOrderPrice !== null) {
              return <b>{ this.$numeral(parseFloat(row.lastOrderPrice)).format('0,0 $') }</b>;
            }
            return null;
          },
          sortable: true,
        },
        {
          label: 'Способ рег.',
          field: ' ',
          cell: ({ row }) => <span>{ row?.partner?.name }</span>,
          sortable: false,
        },
      ];
    },
    offset() {
      return (this.page - 1) * this.limit;
    },
    customers() {
      return _.get(this.payload, 'payload') || [];
    },
    total() {
      return _.get(this.payload, 'total') || null;
    },
    pageCount() {
      return Math.ceil(this.total / this.limit);
    },
    waitPending() {
      return this.$wait.is('fetch.customers');
    },
  },
  methods: {
    ...mapActions({
      fetch: 'customers/ACT_FETCH_CUSTOMERS',
    }),
    getDefaultOrderBy() {
      return [{ column: 'id', descending: 1 }];
    },
    getNormalizedOrderBy() {
      return _.isEqual(this.orderBy, []) ? this.getDefaultOrderBy() : this.orderBy;
    },
    buildListQuery() {
      const query = {};
      const search = String(this.query || '').trim();
      const sort = _.get(this.orderBy, '0.column');
      const descending = _.get(this.orderBy, '0.descending');

      if (this.page > 1) query.page = String(this.page);
      if (search) query.search = search;
      if (sort) {
        query.sort = String(sort);
        query.dir = descending ? 'desc' : 'asc';
      }
      return query;
    },
    normalizeRouteQuery(routeQuery = this.$route.query) {
      const normalized = {};
      ['page', 'search', 'sort', 'dir'].forEach((key) => {
        const value = routeQuery ? routeQuery[key] : undefined;
        if (value !== undefined && value !== null && String(value) !== '') {
          normalized[key] = String(value);
        }
      });
      return normalized;
    },
    syncRouteQuery() {
      if (this.syncRouteLocked) return;
      const nextListQuery = this.buildListQuery();
      const currentListQuery = this.normalizeRouteQuery();
      if (_.isEqual(currentListQuery, nextListQuery)) return;

      const nextQuery = { ...this.$route.query };
      ['page', 'search', 'sort', 'dir'].forEach((key) => delete nextQuery[key]);
      Object.assign(nextQuery, nextListQuery);
      this.$router.replace({
        name: this.$route.name,
        params: this.$route.params,
        query: nextQuery,
      }).catch(() => {});
    },
    applyRouteQuery(routeQuery = this.$route.query) {
      this.syncRouteLocked = true;
      this.page = Math.max(1, Number(routeQuery.page || 1) || 1);
      this.query = String(routeQuery.search || '');
      if (routeQuery.sort) {
        this.orderBy = [{
          column: routeQuery.sort,
          descending: routeQuery.dir === 'asc' ? 0 : 1,
        }];
      } else {
        this.orderBy = [];
      }
      this.$eventBus.$emit('find.customer.value', this.query);
      this.$nextTick(() => {
        this.syncRouteLocked = false;
      });
    },
    resetPageOrFetch() {
      if (this.page !== 1) {
        this.page = 1;
        return;
      }
      this.syncRouteQuery();
      this.fetchPayload();
    },
    applySearch(value) {
      const next = String(value || '').trim();
      if (next === this.query) {
        this.resetPageOrFetch();
        return;
      }
      this.query = next;
    },
    onPageChange(event) {
      this.page = event;
    },
    fetchPayload() {
      this.fetch({
        offset: this.offset,
        limit: this.limit,
        search: String(this.query || '').trim(),
        orderBy: this.getNormalizedOrderBy(),
      });
    },
    onRowClick({ row: event }) {
      this.$router.push({
        name: 'customers.details',
        params: {
          id: event.id,
        },
      });
    },
  },
  watch: {
    page() {
      if (this.syncRouteLocked) return;
      this.syncRouteQuery();
      this.fetchPayload();
    },
    orderBy: {
      deep: true,
      handler() {
        if (this.syncRouteLocked) return;
        this.resetPageOrFetch();
      },
    },
    query() {
      if (this.syncRouteLocked) return;
      this.$eventBus.$emit('find.customer.value', this.query);
      this.resetPageOrFetch();
    },
    '$route.query': {
      deep: true,
      handler(value) {
        const routeState = this.normalizeRouteQuery(value);
        const currentState = this.buildListQuery();
        if (_.isEqual(routeState, currentState)) return;

        this.applyRouteQuery(value);
        this.$nextTick(() => this.fetchPayload());
      },
    },
  },
  mounted() {
    this.$eventBus.$emit('find.customer.value', this.query);
    this.fetchPayload();
    this.$eventBus.$on('find.customer', this.applySearch);
  },
  beforeDestroy() {
    this.$eventBus.$off('find.customer', this.applySearch);
  },
};
</script>
